import type { ClosedDealMatch, DossierCoverage, ParseReport } from '@/services/ingestion';
import { isHeldFor, NAME_HOLD } from './holdReasons';
import type { RequiredGap } from './requiredAtConfirm';

/**
 * Whether Create Deal needs a confirmation first (nd-037 / nd3-19): a record
 * held because its deal name is only the subject line, or one that matches a
 * killed deal. See ConfirmNewDealDialog.
 */

export interface NewDealConfirmation {
  /** The record is held because nobody confirmed its deal name. */
  needsName: boolean;
  /** What the record's dossier suggests instead of the subject line. */
  suggested: string | null;
  /** The subject-line name the engine fell back to. */
  subjectName: string | null;
  killed: ClosedDealMatch[];
  /** The fields required at intake (purpose, city + state, initial and fully
   *  funded amounts) the server found still missing: the dialog asks for each. */
  missing?: RequiredGap[];
}

/** Whether Create Deal on this record needs a confirmation first, and what
 *  it has to show. Null when a plain Create Deal is right. */
export function newDealConfirmation(
  record: { parseReport?: ParseReport | null; extracted?: { property?: string | null } } | null | undefined,
  coverage: Pick<DossierCoverage, 'groups'> | null | undefined,
): NewDealConfirmation | null {
  if (!record) return null;
  const report = record.parseReport;
  const killed = report?.closedDealMatches ?? [];
  // Held for its name among other reasons still needs the name confirmed.
  const needsName = isHeldFor(report, NAME_HOLD) || Boolean(report?.dealName?.lowConfidence);
  const subjectName = (report?.dealName?.value ?? record.extracted?.property ?? '').trim() || null;
  const entry = coverage?.groups
    ?.flatMap((g) => g.fields)
    .find((f) => f.field === 'property_name');
  const offered = typeof entry?.value === 'string' ? entry.value.trim() : '';
  const suggested = offered && offered.toLowerCase() !== (subjectName ?? '').toLowerCase() ? offered : null;
  if (!needsName && killed.length === 0) return null;
  return { needsName, suggested, subjectName, killed };
}

/** The 409 convert answers with when the name is still unconfirmed. */
export function confirmationFromRefusal(detail: unknown): NewDealConfirmation | null {
  if (!detail || typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;
  if (d.code !== 'deal_name_unconfirmed') return null;
  const matches = Array.isArray(d.killed_deal_matches) ? d.killed_deal_matches : [];
  return {
    needsName: true,
    suggested: typeof d.suggested === 'string' ? d.suggested : null,
    subjectName: typeof d.subject_name === 'string' ? d.subject_name : null,
    killed: matches
      .filter((m): m is Record<string, unknown> => Boolean(m) && typeof m === 'object')
      .map((m) => ({
        loanId: String(m.loan_id ?? ''),
        property: typeof m.property_name === 'string' ? m.property_name : undefined,
        basis: String(m.basis ?? ''),
        reason: String(m.reason ?? ''),
        note: String(m.note ?? ''),
      }))
      .filter((m) => m.loanId),
  };
}
